/**
 * Classification des échecs d'upload & backoff (Lot F) — module PUR, testable.
 *
 * Un dépôt fichier-par-fichier doit distinguer TROIS natures d'échec :
 *  - RÉCUPÉRABLE CONNU (réseau coupé, timeout, 429, 500, 502/503/504) : typiquement un
 *    serveur momentanément indisponible (redémarrage Docker, reverse-proxy qui bascule,
 *    disque saturé le temps d'une rotation). On RÉESSAIE avec un backoff exponentiel borné,
 *    SANS LIMITE de tentatives, jusqu'au retour du serveur ;
 *  - FATAL (400 fichier invalide / intégrité KO, 403/404) : rien ne sert de réessayer — le
 *    fichier est marqué en erreur SANS boucle ;
 *  - NON CLASSÉ (tout le reste : une `ZodError` de `…Schema.parse`, une réponse HTML de
 *    portail captif, un `TypeError` inattendu) : on réessaie par prudence — le rejet BRUT de
 *    `fetch` sur coupure réseau tombe ici — mais un nombre BORNÉ de fois
 *    ({@link MAX_UNCLASSIFIED_ATTEMPTS}).
 *
 * ⚠ Pourquoi la troisième catégorie. Sans elle, `isRecoverableError` renvoyait `true` pour
 * TOUTE erreur non explicitement fatale : une réponse malformée (proxy qui intercale une page
 * HTML, portail captif d'un Wi-Fi public) faisait échouer `uploadPreflightResultSchema.parse`,
 * était classée récupérable, et le fichier était réessayé toutes les 30 s À L'INFINI — la
 * boucle `for (;;)` du moteur n'ayant d'autre sortie que le signal d'abandon. Réessayer un
 * `parse` qui échoue de façon déterministe ne peut, par construction, jamais réussir.
 */

/** Nature d'un échec d'upload : à réessayer (transitoire) ou définitif. */
export type UploadFailureKind = 'recoverable' | 'fatal';

/**
 * Erreur d'upload PORTEUSE de sa nature (`kind`) — le moteur de file s'en sert pour
 * décider réessai vs abandon. `status` = code HTTP (`0` = pas de réponse : réseau/timeout).
 * `code` = code métier optionnel (`{ error: { code } }` ou `NETWORK`/`TIMEOUT`/`ABORT`).
 */
export class UploadError extends Error {
  constructor(
    readonly kind: UploadFailureKind,
    readonly status: number,
    readonly code?: string,
  ) {
    super(`upload ${kind} ${status}${code ? ` ${code}` : ''}`);
    this.name = 'UploadError';
  }
}

/**
 * Un code HTTP mérite-t-il un réessai ? `0` (aucune réponse : coupure réseau/timeout),
 * `429` (trop de requêtes), `500`, `502`/`503`/`504` (erreur interne / proxy amont / service
 * indisponible / gateway timeout — l'empreinte typique d'un serveur qui redémarre). Tout le
 * reste (400/401/403/404…) est FATAL : réessayer ne changerait rien.
 *
 * `500` EN FAIT PARTIE, et ce n'est pas de la générosité : le serveur répond 500 sur
 * `write_error` (`apps/api/src/routes/uploads.ts`), c'est-à-dire un échec d'ÉCRITURE DISQUE —
 * typiquement transitoire (volume momentanément saturé, rotation de logs, verrou passager).
 * Le classer fatal condamnait DÉFINITIVEMENT le fichier pour une panne qui se résorbe seule.
 *
 * ⚠ `413` n'apparaît pas ici, et n'a jamais eu à y apparaître : le serveur refuse un fichier
 * trop gros en **400** (`VALIDATION`), pas en 413 — donc déjà fatal par le cas général.
 */
export function isRecoverableStatus(status: number): boolean {
  return (
    status === 0 ||
    status === 429 ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
}

/** Construit une `UploadError` à partir d'un code HTTP, en classant récupérable/fatal. */
export function httpUploadError(status: number, code?: string): UploadError {
  return new UploadError(isRecoverableStatus(status) ? 'recoverable' : 'fatal', status, code);
}

/**
 * Une erreur inconnue (non `UploadError`) est-elle à réessayer ? Un rejet BRUT de `fetch`
 * (pas de réponse HTTP) est un `TypeError` réseau → récupérable. Par prudence, tout ce qui
 * n'est pas explicitement `UploadError('fatal')` est traité comme récupérable (résilience
 * au serveur indisponible) — le moteur borne malgré tout le rythme via le backoff, ET le
 * NOMBRE de tentatives via {@link isKnownTransient} / {@link MAX_UNCLASSIFIED_ATTEMPTS}.
 */
export function isRecoverableError(err: unknown): boolean {
  if (err instanceof UploadError) return err.kind === 'recoverable';
  return true;
}

/**
 * L'erreur est-elle EXPLICITEMENT classée transitoire, c'est-à-dire une {@link UploadError}
 * construite avec un statut que {@link isRecoverableStatus} reconnaît ? Seules celles-là
 * méritent un réessai SANS LIMITE : on sait de quoi il s'agit (serveur qui redémarre) et on
 * sait que l'attente est le bon remède.
 *
 * Tout le reste — `ZodError`, `TypeError`, erreur d'un module tiers — est réessayé un nombre
 * BORNÉ de fois : ces erreurs sont majoritairement DÉTERMINISTES (une réponse malformée le
 * restera), et une boucle infinie sur elles est un blocage silencieux, pas de la résilience.
 */
export function isKnownTransient(err: unknown): boolean {
  return err instanceof UploadError && err.kind === 'recoverable';
}

/**
 * Nombre maximal de tentatives pour une erreur NON CLASSÉE (cf. {@link isKnownTransient}).
 * Assez pour absorber un aléa ponctuel (une réponse tronquée, un hoquet de proxy), assez peu
 * pour que l'utilisateur voie une erreur en moins d'une minute au lieu d'une file figée.
 */
export const MAX_UNCLASSIFIED_ATTEMPTS = 4;

/**
 * Délai de backoff EXPONENTIEL borné (ms), déterministe : `base·2^(attempt-1)` plafonné
 * à `cap`. `attempt` commence à 1 (1ᵉ réessai). Ex. (base 1000, cap 30000) :
 * 1→1000, 2→2000, 3→4000, 4→8000, 5→16000, ≥6→30000. Le JITTER éventuel (anti-effet de
 * meute) est appliqué PAR L'APPELANT, pour garder cette fonction pure et testable.
 */
export function backoffDelayMs(attempt: number, base = 1000, cap = 30000): number {
  const exp = base * 2 ** Math.max(0, attempt - 1);
  return Math.min(cap, exp);
}
